import { Show, type JSXElement } from 'solid-js'
import { BalancePills } from './balance-pills'
import {
  RewardRevealBackdrop,
  type RewardRevealVariant,
} from './reward-reveal-backdrop'

/**
 * The shared chrome of a full-screen reward reveal: balance pills, title,
 * the 40vh reward anchor with the rive backdrop and the prize's fade/zoom
 * entrance, caption, and the white CTA. Behaviour stays with the caller —
 * the currency reveal's flyer burst renders as a sibling of this scaffold
 * and aims at the pill/anchor refs. A reveal of several pages keeps one
 * scaffold and passes the showing page, whose every change replays the
 * backdrop and the prize's entrance.
 */
export function RewardRevealScaffold(props: {
  balanceTestIdPrefix: string
  caption: string
  captionTestId?: string
  children: JSXElement
  coinPillRef?: (element: HTMLDivElement) => void
  coinValue: number
  ctaDisabled?: boolean
  ctaLabel: string
  ctaTestId: string
  gemPillRef?: (element: HTMLDivElement) => void
  gemValue: number
  onCta: () => void
  // A multi-page reveal's showing page (data-page); each change replays the
  // backdrop and the prize's entrance. Absent for a single-page reveal.
  page?: number
  phase?: 'flight' | 'ready'
  // Under the caption, e.g. a multi-page reveal's progress indicator.
  progress?: JSXElement
  rewardAnchorRef?: (element: HTMLDivElement) => void
  testId: string
  title: string
  variant: RewardRevealVariant
}) {
  return (
    <section
      data-testid={props.testId}
      data-page={props.page}
      data-phase={props.phase}
      class="relative flex size-full flex-col items-center pb-12 pt-[calc(8px+var(--safe-area-top))] text-center text-white"
    >
      <BalancePills
        balances={{ coin: props.coinValue, gem: props.gemValue }}
        class="ml-auto"
        coinPillRef={props.coinPillRef}
        gemPillRef={props.gemPillRef}
        inverted
        testIdPrefix={props.balanceTestIdPrefix}
      />

      <h2 class="mt-14 whitespace-pre-line text-xl font-semibold">
        {props.title}
      </h2>
      <div class="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
        <div
          ref={props.rewardAnchorRef}
          class="relative mx-auto flex h-[40vh] w-full max-w-[40vh] items-center justify-center"
        >
          <Show when={String(props.page ?? 'reward')} keyed>
            {(_key) => (
              <>
                <RewardRevealBackdrop variant={props.variant} />
                <div class="absolute inset-0 m-auto size-1/2 duration-500 ease-out animate-in fade-in zoom-in-90">
                  {props.children}
                </div>
              </>
            )}
          </Show>
        </div>
        <p
          data-testid={props.captionTestId}
          class="mt-10 text-2xl font-bold [text-shadow:0_0_15px_rgba(255,255,255,0.7)]"
        >
          {props.caption}
        </p>
        {props.progress}
      </div>

      <button
        type="button"
        data-testid={props.ctaTestId}
        disabled={props.ctaDisabled}
        onClick={() => props.onCta()}
        class="h-[52px] w-full max-w-[640px] rounded-full bg-white text-base font-bold text-[#176361] disabled:bg-white/45 disabled:text-[#176361]/65"
      >
        {props.ctaLabel}
      </button>
    </section>
  )
}
